import { Box, Flex, Link, List, Stack, useToken } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaEye, FaUnlock } from 'react-icons/fa6';
import { MdSupportAgent } from 'react-icons/md';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftOverlayLoader from '@/components/Atoms/FaceliftOverlayLoader/FaceliftOverlayLoader';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftDialogWithExitConfirmation from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import Show from '@/components/Molecules/Show/Show';
import DataTable from '@/components/Organisms/DataTable';
import type { onFilterApplyProps } from '@/components/Organisms/DataTable/DataTable.types';
import {
	useDeleteInvoicePaymentLock,
	useGetInvoicePaymentsLock,
} from '@/hooks/service-hooks/invoicePaymentLock';
import { useAppTour } from '@/hooks/useAppTour';
import usePermission from '@/hooks/usePermission';
import { getInvoicePaymentsLock } from '@/services/invoicePaymentLock';
import type { InvoicePaymentLock } from '@/types/payment';

interface LineItem {
	invoiceNumber: string;
	quantity: number;
	price_data: {
		currency: string;
		unit_amount: number;
		product_data: {
			name: string;
			description: string;
			clinicName: string;
		};
	};
}

import FaceliftTruncatedTextWithTooltip from '@/components/Atoms/TruncatedTextWithTooltip/FaceliftTruncatedTextWithTooltip';
import FaceliftInvoiceViewPDF from '@/components/Molecules/FaceliftInvoiceViewPDF';
import { useGetAccounts } from '@/hooks/service-hooks/accounts';
import { useClinicFilter } from '@/hooks/useClinicFilter';
import { useInvoicePDFViewer } from '@/hooks/useInvoicePDFViewer';
import type { FilterType } from '@/types/table';
import { formatDateToString } from '@/utils/dateUtils';
import {
	generateCSVFromTable,
	generateExcelFromTable,
} from '@/utils/exportFiles';
import DetailsView from '../DetailsView';
import { SupportTicketForm } from '../SupportTickets/SupportTicketForm';
import {
	columns as paymentColumns,
	statusesFilterOptionsInvoicePaymentLock,
} from './invoicePaymentLock';

const CLINIC_KEY = 'clinicGUID';

/**
 * InvoicePaymentLockTable Component
 *
 * Displays active payment sessions and locked invoices in a data table.
 * Shows which users currently have invoices locked for payment processing.
 *
 * Features:
 * - Real-time display of active payment sessions
 * - Shows invoice number, clinic, user info, and session time
 * - Clickable payment link for each session
 * - Export functionality (CSV, Excel)
 * - Integrated with app tour system
 */
const InvoicePaymentLockTable = () => {
	const statusOptions: SelectItem[] = statusesFilterOptionsInvoicePaymentLock;
	const [brand600] = useToken('colors', ['brand.600']);
	const { hasPermission } = usePermission();
	// Shared clinic filter helpers (key used by Invoice Payment Lock API)
	const { defaultClinicIds, mapGlobalFilters } = useClinicFilter(CLINIC_KEY);

	// Table filter state: manages pagination, search, sorting, and tab selection
	const [filters, setFilters] = useState<FilterType>({
		page: 1,
		limit: 15,
		columnsFilters: '[]',
		search: '',
		enabled: defaultClinicIds.length > 0,
		tab: '',
		sort: '',
	});

	// Fetch payment lock data with React Query
	const { data, isFetching } = useGetInvoicePaymentsLock(filters);

	const columns = useMemo(() => paymentColumns, []);

	// Modal state for viewing selected clinics
	const [showClinicsModal, setShowClinicsModal] = useState(false);
	const [selectedClinics, setSelectedClinics] = useState<string>('');

	// Modal state for viewing invoice line items details
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	const [selectedLineItems, setSelectedLineItems] = useState<LineItem[]>([]);

	// Modal state for support ticket
	const [showSupportTicketModal, setShowSupportTicketModal] = useState(false);
	const [supportTicketData, setSupportTicketData] = useState<{
		invoiceNumber: string;
		clinic: SelectItem | null;
	} | null>(null);
	const [isSupportTicketFormDirty, setIsSupportTicketFormDirty] =
		useState(false);
	const [supportTicketExitDraftAction, setSupportTicketExitDraftAction] =
		useState<ExitConfirmationDraftAction | null>(null);

	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

	// Extract clinic GUIDs from current filters for accounts fetch
	const selectedClinicGUIDs = useMemo(() => {
		const parsedFilters = JSON.parse(filters.globalFilters || '[]');
		const clinicFilter = parsedFilters.find(
			(filter: { key: string; values: string[] }) => filter.key === CLINIC_KEY,
		);
		return clinicFilter?.values || defaultClinicIds;
	}, [filters.globalFilters, defaultClinicIds]);

	// Use custom hook for PDF viewing functionality
	const {
		viewInvoiceModal,
		pdfViewerUrl,
		isFetchingPDF,
		isEmptyOrUnavailable,
		isPdfLoadError,
		setViewInvoiceModal,
		setPdfViewerUrl,
		setSelectedInvoiceNumber,
		setSelectedClinicQBOId,
	} = useInvoicePDFViewer();

	const { data: clinicsData, isFetching: isClinicsFetching } = useGetAccounts({
		page: 1,
		limit: 5000,
		enabled: selectedClinicGUIDs.length > 0,
		ids: selectedClinicGUIDs,
	});

	// State for selected invoices in the table
	const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
	const [message, setMessage] = useState<string>('');
	const [oneInvoiceSelected, setOneInvoiceSelected] =
		useState<InvoicePaymentLock | null>(null);
	const { mutateAsync: deletePaymentLock, isLoading: isDeleting } =
		useDeleteInvoicePaymentLock(
			async () => {
				// onSuccess callback
			},
			(errorMessage: string) => {
				toast.error(errorMessage);
			},
		);

	// Refs for app tour integration - points to table container and select column
	const [paymentsTableRef, paymentsSelectToPayColumnRef] = [
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLTableCellElement | null>(null),
	];

	// Track when select column element is mounted for tour system
	const [selectColumnElement, setSelectColumnElement] =
		useState<HTMLElement | null>(null);

	// Update select column ref when element is ready
	const handleSelectColumnRefReady = useCallback(
		(element: HTMLElement | null) => {
			if (element && paymentsSelectToPayColumnRef.current !== element) {
				paymentsSelectToPayColumnRef.current = element as HTMLTableCellElement;
			}
			setSelectColumnElement(element);
		},
		[paymentsSelectToPayColumnRef],
	);

	// Register elements for guided app tour
	useAppTour({
		elements: {
			paymentsTableRef: paymentsTableRef.current,
			paymentsSelectToPayColumnRef: selectColumnElement,
		},
	});

	/**
	 * Handle viewing selected clinics in modal
	 */
	const handleViewClinics = useCallback((clinics: string | undefined) => {
		if (clinics) {
			setSelectedClinics(clinics);
			setShowClinicsModal(true);
		} else {
			toast.error('No clinics found');
		}
	}, []);

	/**
	 * Handle viewing invoice PDF in modal (from table link click)
	 * Sets the invoice number and clinic QBO ID to trigger the PDF fetch
	 */
	const handleViewInvoice = useCallback(
		(invoiceNumber: string, clinicQBOId: string) => {
			if (!clinicQBOId) {
				toast.error('Clinic QBO ID not found for the selected invoice');
				return;
			}

			// Reset previous PDF URL
			setPdfViewerUrl('');

			// Set invoice details to trigger the PDF fetch via useInvoicePDFViewer hook
			setSelectedInvoiceNumber(invoiceNumber);
			setSelectedClinicQBOId(clinicQBOId);
			setViewInvoiceModal(true);
		},
		[
			setPdfViewerUrl,
			setSelectedInvoiceNumber,
			setSelectedClinicQBOId,
			setViewInvoiceModal,
		],
	);

	/**
	 * Transform payment lock data for table display
	 * Converts raw API data into table-ready format with:
	 * - Formatted session timestamps
	 * - Clickable payment links
	 * - User and clinic information
	 * - Action button to view selected clinics
	 */
	const transformedData = useMemo(() => {
		if (!data?.items || !clinicsData?.items) return [];
		const invoicePaymentLockData = data.items;
		const clinicsQBOIds: { [clinicGUID: string]: string } =
			clinicsData.items.reduce(
				(acc, clinic) => {
					acc[`{${clinic.accountid}}`] = clinic.ta_qbocustomerid;
					return acc;
				},
				{} as { [clinicGUID: string]: string },
			);

		// Create a map of clinic GUID to SelectItem for support ticket
		const clinicsMap: { [clinicGUID: string]: SelectItem } =
			clinicsData.items.reduce(
				(acc, clinic) => {
					acc[`{${clinic.accountid}}`] = {
						value: clinic.accountid,
						label: clinic.name,
					};
					return acc;
				},
				{} as { [clinicGUID: string]: SelectItem },
			);

		return invoicePaymentLockData.map((item) => {
			return {
				id: item.sessionId || item.paymentLink,
				invoice: item.invoiceNumber,
				invoiceNumber: (
					<Link
						color={'brand.solidSecondary'}
						fontWeight={'bold'}
						onClick={() =>
							handleViewInvoice(
								item.invoiceNumber,
								clinicsQBOIds[item.clinicGUID] || '',
							)
						}
					>
						{item.invoiceNumber}
					</Link>
				),
				invoiceNumbers: item.invoiceNumbers,
				clinicName: (
					<FaceliftTruncatedTextWithTooltip
						text={item.clinicName || '--'}
						maxLength={30}
					/>
				),
				userName: (
					<FaceliftTruncatedTextWithTooltip
						text={item.userName || '-'}
						maxLength={30}
					/>
				),
				userEmail: item.userEmail,
				invoiceLineItems: item.invoiceLineItems || '',
				userSelectedClinics: item.userSelectedClinics && (
					<FaceliftButton
						size="sm"
						colorScheme="blue"
						onClick={() => handleViewClinics(item.userSelectedClinics)}
					>
						View Clinics
					</FaceliftButton>
				),
				paymentLink: item.paymentLink,
				sessionId: item.sessionId,
				sessionTime: item.sessionTime
					? formatDateToString(new Date(item.sessionTime))
					: '',
				url: (
					<Link href={item.url} color={'brand.solidSecondary'}>
						Link
					</Link>
				),
				qboId: clinicsQBOIds ? clinicsQBOIds[item.clinicGUID] : '',
				aging: `${
					item.sessionTime
						? Math.floor(
								(Date.now() - new Date(item.sessionTime).getTime()) /
									(1000 * 60 * 60 * 24),
							)
						: '0'
				} days`,
				// Store clinic SelectItem for support ticket
				clinicSelectItem: clinicsMap[item.clinicGUID] || null,
			};
		});
	}, [data, clinicsData, handleViewClinics, handleViewInvoice]);

	/**
	 * Transform payment lock data for export
	 * Converts JSX elements to plain text for CSV/Excel compatibility
	 */
	const transformedDataToExport = (
		invoicePaymentLockData: InvoicePaymentLock[],
	) => {
		if (!invoicePaymentLockData.length) return [];

		return invoicePaymentLockData.map((item) => {
			return {
				id: item.sessionId || item.paymentLink,
				invoiceNumber: item.invoiceNumber,
				clinicName: item.clinicName,
				userName: item.userName,
				userEmail: item.userEmail,
				userSelectedClinics: item.userSelectedClinics,
				sessionTime: formatDateToString(new Date(item.sessionTime)),
				url: item.url,
				paymentLink: item.paymentLink,
				sessionId: item.sessionId,
				aging: `${
					item.sessionTime
						? Math.floor(
								(Date.now() - new Date(item.sessionTime).getTime()) /
									(1000 * 60 * 60 * 24),
							)
						: '0'
				} days`,
			};
		});
	};

	/**
	 * Handle filter changes from DataTable
	 * Updates filter state to trigger new data fetch with applied filters
	 */
	const onFilterApply = useCallback(
		({
			page,
			limit,
			status,
			search = '',
			sort = null,
			columnsFilters = [],
		}: onFilterApplyProps) => {

			setFilters({
				page,
				limit,
				columnsFilters: JSON.stringify(columnsFilters),
				search,
				enabled: defaultClinicIds.length > 0,
				status: status?.value || 'session',
				sort: sort ? `${sort.key}|${sort.direction}` : '',
			});
		},
		[mapGlobalFilters],
	);

	/**
	 * Fetch all payment lock records for export
	 * Iterates through all pages to get complete dataset with current filters applied
	 * @returns Complete payment lock data formatted for export
	 */
	const fetchAllDataToExport = async () => {
		let allItems: InvoicePaymentLock[] = [];
		let page = 1;
		let hasMore = true;

		while (hasMore) {
			const response = await getInvoicePaymentsLock(
				page,
				9999,
				filters.globalFilters,
				filters.columnsFilters,
				filters.search,
				(filters.status as string) || '',
			);

			if (response?.items?.length) {
				allItems = allItems.concat(response.items);
				const totalItems = response?.meta?.totalItems || 0;
				hasMore = allItems.length < totalItems;
				page += 1;
			} else {
				hasMore = false;
			}
		}

		const invoiceDataForExcel = { items: allItems };
		const transformedData = transformedDataToExport(
			invoiceDataForExcel.items || [],
		);

		return transformedData;
	};

	/**
	 * Export Payment Links to CSV file
	 */
	const exportAllAsCSV = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateCSVFromTable(
				columns,
				transformedInvoiceData,
				`PaymentLocks-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting CSV:', error);
			toast.error('Failed to export CSV');
		}
	};

	/**
	 * Export Payment Links to Excel file
	 */
	const exportAllAsExcel = async () => {
		try {
			const transformedInvoiceData = await fetchAllDataToExport();
			generateExcelFromTable(
				columns,
				transformedInvoiceData,
				`PaymentLocks-all`,
				true,
			);
		} catch (error) {
			console.error('Error exporting Excel:', error);
			toast.error('Failed to export Excel');
		}
	};

	/**
	 * Handle confirmation modal action
	 * Determines whether to unlock a single invoice or multiple invoices
	 * based on which state is populated, then resets modal state
	 */
	const handleConfirmModal = () => {
		if (oneInvoiceSelected) {
			handleUnlockInvoice(oneInvoiceSelected);
		} else if (selectedInvoices.length) {
			handleUnlockMultipleInvoices();
		}
		setShowConfirmModal(false);
		setMessage('');
	};

	/**
	 * Unlock a single invoice payment lock
	 * Extracts the reference ID (url or sessionId) and calls delete API
	 * Shows success/error toast based on result
	 *
	 * @param invoice - The invoice payment lock to unlock
	 */
	const handleUnlockInvoice = useCallback(
		async (invoice: InvoicePaymentLock | null) => {
			// Get reference ID from either url or sessionId field
			const referenceInvoice = invoice?.paymentLink || invoice?.sessionId;
			if (!referenceInvoice) {
				toast.error('Failed to unlock invoice. Please try again.');
				return;
			}
			// Call API to delete the payment lock
			await deletePaymentLock({ id: [referenceInvoice] });
			// Clear selected invoice state
			setOneInvoiceSelected(null);
			toast.success('Invoice unlocked successfully');
		},
		[deletePaymentLock],
	);

	/**
	 * Unlock multiple selected invoices
	 * Collects IDs from selected rows and calls delete API in batch
	 * Shows success/error toast based on result
	 */
	const handleUnlockMultipleInvoices = useCallback(async () => {
		// Map selected invoice IDs from table state to actual reference IDs
		const referenceInvoices = transformedData
			.filter((invoice) => selectedInvoices.includes(invoice.id))
			.map((invoice) => invoice.id as string);

		if (referenceInvoices.length === 0) {
			toast.error('Failed to unlock invoices. Please try again.');
			return;
		}

		// Call API to delete multiple Payment Links
		await deletePaymentLock({ id: referenceInvoices });
		// Clear selected invoices state
		setSelectedInvoices([]);
		toast.success('Invoices unlocked successfully');
	}, [deletePaymentLock, selectedInvoices, transformedData]);

	const onSelection = (ids: string[]) => {
		// Update selected invoices state
		setSelectedInvoices(ids);
	};

	return (
		<Box
			p={{ base: 4, md: 8 }}
			bg={{ base: '{colors.gray.50}', _dark: '{colors.gray.900}' }}
			borderRadius="md"
			boxShadow="sm"
		>
			<Show when={isDeleting}>
				<FaceliftOverlayLoader />
			</Show>

			{/* This Dialog is used to view a single invoice PDF (from table link click) */}
			<FaceliftInvoiceViewPDF
				viewInvoiceModal={viewInvoiceModal}
				isLoading={isFetchingPDF}
				pdfViewerUrl={pdfViewerUrl}
				isEmptyOrUnavailable={isEmptyOrUnavailable}
				isLoadError={isPdfLoadError}
				onOpenChange={() => {
					setViewInvoiceModal(!viewInvoiceModal);
					// Clear state when modal closes
					if (!viewInvoiceModal) {
						setSelectedInvoiceNumber('');
						setSelectedClinicQBOId('');
						setPdfViewerUrl('');
					}
				}}
			/>

			{/* Modal for confirming unlock action */}
			<FaceliftDialog
				open={showConfirmModal}
				onOpenChange={() => setShowConfirmModal(false)}
				title="Payment Sessions"
				size="md"
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
				footer={
					<>
						<FaceliftButton
							variant="outline"
							colorScheme="gray"
							bg="transparent"
							outline={'none'}
							color="inherit"
							border="1px solid"
							width={'32'}
							onClick={() => setShowConfirmModal(false)}
						>
							CANCEL
						</FaceliftButton>

						<FaceliftButton
							onClick={handleConfirmModal}
							cursor="pointer"
							width={'32'}
							outline={'none'}
						>
							UNLOCK
						</FaceliftButton>
					</>
				}
			>
				{message}
			</FaceliftDialog>

			{/* Modal for viewing selected clinics */}
			<FaceliftDialog
				showBackBtn={true}
				showCloseBtn={false}
				open={showClinicsModal}
				onOpenChange={setShowClinicsModal}
				title="Selected Clinics"
				size="md"
			>
				<Box maxH="70vh" overflowY="auto">
					<FaceliftText fontSize="sm" fontWeight="600" mb={3} color="gray.600">
						User selected the following clinics:
					</FaceliftText>
					<List.Root gap={2} maxH="70vh" overflowY="auto">
						{selectedClinics?.split('||').map((clinic) => {
							const trimmedClinic = clinic.trim();
							return (
								<List.Item
									key={trimmedClinic}
									p={2}
									borderRadius="md"
									bg={{ base: 'gray.50', _dark: 'gray.800' }}
									borderWidth="1px"
									borderColor={{ base: 'gray.200', _dark: 'gray.700' }}
								>
									<FaceliftText fontSize="sm" fontWeight="500">
										{trimmedClinic}
									</FaceliftText>
								</List.Item>
							);
						})}
					</List.Root>
				</Box>
			</FaceliftDialog>

			{/* Modal for viewing invoice line items details */}
			<FaceliftDialog
				showBackBtn={true}
				open={showDetailsModal}
				onOpenChange={setShowDetailsModal}
				title="Invoice Description"
				size="md"
				sx={{ boxShadow: `0 0 20px 0px ${brand600}` }}
			>
				<Show when={selectedLineItems.length > 0}>
					<Box maxH="80vh" overflowX="auto">
						<Box px={2}>
							{/* No Line Items */}
							<Show when={selectedLineItems.length === 0}>
								<Box px={2} textAlign="center">
									<FaceliftText fontSize="lg" fontWeight="bold">
										No Line Items Found
									</FaceliftText>
									<FaceliftText fontSize="sm" color="gray.500">
										No products were found for this invoice.
									</FaceliftText>
								</Box>
							</Show>

							{/* Table View */}
							<Show when={selectedLineItems.length > 0}>
								<Stack gap={4}>
									{selectedLineItems?.map((item, index) => (
										<Box
											key={item.price_data.product_data.name || index}
											px={2}
										>
											<Flex
												mb={4}
												pb={1}
												align="center"
												justify="space-between"
											>
												<FaceliftText fontSize="lg" fontWeight="bold">
													Invoice# {item.invoiceNumber}
												</FaceliftText>
											</Flex>

											{/* Details Grid */}
											<DetailsView
												data={[
													{
														data: [
															{
																label: 'Clinic Name',
																value: item.price_data.product_data.clinicName,
															},
															{
																label: 'Product Name',
																value: item.price_data.product_data.name,
															},
															{
																label: 'Description',
																value: item.price_data.product_data.description,
															},
															{
																label: 'Amount',
																value: `${(
																	item.price_data.unit_amount / 100
																).toLocaleString('en-US', {
																	style: 'currency',
																	currency: item.price_data.currency,
																})}`,
															},
														],
													},
												]}
												gridConfig={{ base: '1fr', md: '1fr' }}
												gap={{ base: 3, md: 3 }}
											/>
										</Box>
									))}
								</Stack>
							</Show>
						</Box>
					</Box>
				</Show>

				<Show when={selectedLineItems.length === 0}>
					<Box px={2}>
						<FaceliftText fontSize="sm" color="gray.500">
							No line items found
						</FaceliftText>
					</Box>
				</Show>
			</FaceliftDialog>

			{/* Support Ticket Modal */}
			<FaceliftDialogWithExitConfirmation
				open={showSupportTicketModal}
				onOpenChange={(openState: boolean) => {
					if (!openState) {
						setShowSupportTicketModal(false);
						setSupportTicketData(null);
						setIsSupportTicketFormDirty(false);
					}
				}}
				title="Create Support Ticket"
				showBackBtn={true}
				size="xl"
				sx={{
					boxShadow: `0 0 20px 0px ${brand600}`,
					width: '840px',
					maxWidth: '95vw',
					overflowY: 'auto',
				}}
				isDirty={isSupportTicketFormDirty}
				saveDraftAction={supportTicketExitDraftAction}
			>
				<SupportTicketForm
					mode="create"
					initialData={{
						clinic: supportTicketData?.clinic || null,
						type: { value: '953870007', label: 'Tech' },
						subject: supportTicketData?.invoiceNumber || '',
						status: { value: '1', label: 'In Progress' },
					}}
					onSuccess={() => {
						setShowSupportTicketModal(false);
						setSupportTicketData(null);
						setIsSupportTicketFormDirty(false);
					}}
					onIsDirtyChange={setIsSupportTicketFormDirty}
					onExitConfirmationDraftActionChange={
						showSupportTicketModal
							? setSupportTicketExitDraftAction
							: undefined
					}
				/>
			</FaceliftDialogWithExitConfirmation>

			<DataTable
				tableName="Payment Links"
				ref={paymentsTableRef}
				loading={isFetching || isClinicsFetching}
				items={transformedData}
				columns={columns}
				customExportItemsData={transformedDataToExport(data?.items || [])}
				topLabel="Payment Sessions"
				totalCount={data?.meta.totalItems || 0}
				onFilterApply={onFilterApply}
				isSelectable
				onSelection={onSelection}
				globalActions={[
					{
						title: 'Unlock',
						action: () => {
							setMessage(
								`Are you sure you want to unlock invoice/s #${
									selectedInvoices.length
										? transformedData
												.filter((invoice) =>
													selectedInvoices.includes(invoice.id),
												)
												.map((item) => item.invoice)
												.join(',')
										: ''
								}. Select UNLOCK to confirm`,
							);
							setShowConfirmModal(true);
						},
					},
				]}
				bordered
				searchable
				hideShowColumn
				expandable
				paginated
				fullscreen
				actions={[
					{
						label: 'View Details',
						icon: <FaEye />,
						actionFn: (item) => {
							const selectedInvoice = item as unknown as InvoicePaymentLock;
							const lineItems = (
								(selectedInvoice.invoiceLineItems
									? JSON.parse(selectedInvoice.invoiceLineItems)
									: []) as LineItem[]
							).map((lineItem: LineItem) => {
								const invoiceNumber =
									lineItem.price_data.product_data.name?.split(': |')[0] || '';
								return { ...lineItem, invoiceNumber: invoiceNumber };
							});
							setSelectedLineItems(lineItems);
							setShowDetailsModal(true);
						},
					},
					{
						label: 'Support Ticket',
						icon: <MdSupportAgent />,
						actionFn: (item) => {
							const invoiceNumber = (item as any).invoice || '';
							const clinic = (item as any).clinicSelectItem || null;

							setSupportTicketData({
								invoiceNumber,
								clinic,
							});
							setIsSupportTicketFormDirty(false);
							setShowSupportTicketModal(true);
						},
						show: () => hasPermission(['SupportTickets:Create']),
					},
					{
						label: 'Unlock',
						icon: <FaUnlock />,
						actionFn: (item) => {
							const selectedItem = item as unknown as InvoicePaymentLock;
							const firstInvoice = selectedItem.invoice;
							const linkedInvoices =
								selectedItem.invoiceNumbers
									?.split(',')
									.filter((el) => el !== firstInvoice) || [];
							if (linkedInvoices.length >= 1) {
								setMessage(
									`This invoice #${firstInvoice} is locked to other/s. Unlocking this invoice/s will also unlock the following invoice/s #${linkedInvoices.map((el) => el).join(', #')}. Select UNLOCK to confirm`,
								);
							} else {
								setMessage(
									`Are you sure you want to unlock invoice #${firstInvoice}?`,
								);
							}
							setOneInvoiceSelected(selectedItem);
							setShowConfirmModal(true);
						},
					},
				]}
				exportable
				statusFilters={statusOptions}
				hideAllStatusFilter={true}
				csv
				excel
				selectColumnRef={paymentsSelectToPayColumnRef}
				onSelectColumnRefReady={handleSelectColumnRefReady}
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
			/>
		</Box>
	);
};

export default InvoicePaymentLockTable;
